import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { fleetService } from '../../services/fleetService';
import { Fleet } from '../../types';
import FleetReservationSettings from '../reservation/FleetReservationSettings';

interface FleetManagementProps {
  organizationId: string;
}

const FleetManagement: React.FC<FleetManagementProps> = ({ organizationId }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [fleets, setFleets] = useState<Fleet[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentFleet, setCurrentFleet] = useState<Fleet | null>(null);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [tabValue, setTabValue] = useState(0);

  const loadFleets = async () => {
    try {
      setLoading(true);
      const fleetsList = await fleetService.getFleets(organizationId);
      setFleets(fleetsList);
      setError(null);
    } catch (err) {
      console.error('Error loading fleets:', err);
      setError(t('fleet.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFleets();
  }, [organizationId]);

  const handleOpenDialog = (fleet: Fleet | null = null) => {
    setCurrentFleet(fleet);
    setFormData({
      name: fleet?.name || '',
      description: fleet?.description || '',
    });
    setTabValue(0); // Reset to first tab
    setOpenDialog(true);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentFleet(null);
    setFormData({ name: '', description: '' });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      return;
    }

    try {
      if (currentFleet) {
        // Update existing fleet
        await fleetService.updateFleet(
          currentFleet.id,
          {
            name: formData.name.trim(),
            description: formData.description.trim(),
          },
          user?.id || ''
        );
      } else {
        // Create new fleet
        await fleetService.createFleet(
          organizationId,
          {
            name: formData.name.trim(),
            description: formData.description.trim(),
          },
          user?.id || ''
        );
      }
      handleCloseDialog();
      loadFleets();
    } catch (err) {
      console.error('Error saving fleet:', err);
      setError(t('fleet.errors.saveFailed'));
    }
  };

  const handleSaveReservationSettings = async (settings: {
    publicLinkEnabled: boolean;
    requireEmailValidation: boolean;
    allowedEmailDomains: string[];
  }) => {
    if (!currentFleet) return;

    try {
      await fleetService.updateFleetReservationSettings(
        currentFleet.id,
        settings,
        user?.id || ''
      );

      // Si le lien public est activé mais qu'il n'y a pas encore de publicLinkId, en générer un
      if (settings.publicLinkEnabled && !currentFleet.publicLinkId) {
        await fleetService.generatePublicLink(currentFleet.id);
      }

      // Recharger les flottes pour mettre à jour les données
      loadFleets();
      
      // Mettre à jour le currentFleet avec les nouvelles données
      const updatedFleet = await fleetService.getFleet(currentFleet.id);
      if (updatedFleet) {
        setCurrentFleet(updatedFleet);
      }
    } catch (err) {
      console.error('Error saving reservation settings:', err);
      setError(t('fleet.errors.saveSettingsFailed', 'Échec de l\'enregistrement des paramètres de réservation'));
    }
  };

  const handleGenerateLink = async (fleetId: string) => {
    try {
      await fleetService.generatePublicLink(fleetId);
      
      // Recharger les flottes pour mettre à jour les données
      loadFleets();
      
      // Mettre à jour le currentFleet avec les nouvelles données
      if (currentFleet && currentFleet.id === fleetId) {
        const updatedFleet = await fleetService.getFleet(fleetId);
        if (updatedFleet) {
          setCurrentFleet(updatedFleet);
        }
      }
    } catch (err) {
      console.error('Error generating public link:', err);
      setError(t('fleet.errors.generateLinkFailed', 'Échec de la génération du lien public'));
    }
  };

  const handleDeleteFleet = async (id: string) => {
    if (window.confirm(t('fleet.confirmDelete'))) {
      try {
        await fleetService.deleteFleet(id);
        loadFleets();
      } catch (err) {
        console.error('Error deleting fleet:', err);
        setError(t('fleet.errors.deleteFailed'));
      }
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">{t('fleet.management')}</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          {t('fleet.addFleet')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('fleet.name')}</TableCell>
              <TableCell>{t('fleet.description')}</TableCell>
              <TableCell>{t('common.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {fleets.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  {t('fleet.noFleets')}
                </TableCell>
              </TableRow>
            ) : (
              fleets.map((fleet) => (
                <TableRow key={fleet.id}>
                  <TableCell>{fleet.name}</TableCell>
                  <TableCell>{fleet.description || '-'}</TableCell>
                  <TableCell>
                    <IconButton
                      color="primary"
                      onClick={() => handleOpenDialog(fleet)}
                      size="small"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => handleDeleteFleet(fleet.id)}
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {currentFleet ? t('fleet.editFleet') : t('fleet.addFleet')}
        </DialogTitle>
        
        {currentFleet && (
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label={t('fleet.generalInfo', 'Informations générales')} />
            <Tab label={t('fleet.reservationSettings', 'Paramètres de réservation')} />
          </Tabs>
        )}
        
        {tabValue === 0 && (
          <form onSubmit={handleSubmit}>
            <DialogContent>
              <TextField
                autoFocus
                margin="dense"
                label={t('fleet.name')}
                fullWidth
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
              <TextField
                margin="dense"
                label={t('fleet.description')}
                fullWidth
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>{t('common.cancel')}</Button>
              <Button type="submit" variant="contained" color="primary">
                {t('common.save')}
              </Button>
            </DialogActions>
          </form>
        )}
        
        {tabValue === 1 && currentFleet && (
          <DialogContent>
            <FleetReservationSettings 
              fleet={currentFleet} 
              onSave={handleSaveReservationSettings}
              onGenerateLink={handleGenerateLink}
            />
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button onClick={handleCloseDialog} sx={{ mr: 1 }}>
                {t('common.close')}
              </Button>
            </Box>
          </DialogContent>
        )}
      </Dialog>
    </Box>
  );
};

export default FleetManagement;
